/**
 * RecordPopup — a record opened in place, over the chat, never a page hop.
 *
 * Runs and spans open the product's full run view through the chat page's
 * own entity opener. Every other entity opens here: the row's own fields,
 * formatted the way the table formats them, with the one explicit way out —
 * "Open full page" — at the bottom. Akshit (3 Sep): "the pop-up of the run
 * should open there… same for spans and conversations and issues and
 * everything… not taking the user to the other page".
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ExternalLink } from 'lucide-react';
import { trodoAnalyticsTheme as theme } from 'features/analytics/studio/theme';
import { formatCell } from './GenericRecordsTable';
import { RECORD_COLUMNS, ROW_LINKS } from './recordColumns';
import { besideClick } from './recordsSurface';

const SKIP = new Set(['team_id', 'embedding', 'centroid', 'metadata', 'attributes', 'input_embedding', 'output_embedding']);

function titleOf(entity, row) {
  return row.name || row.title || row.label || row.agent_name || row.distinct_id || row.conversation_id || row.run_id || row.span_id || row.id || entity;
}

export default function RecordPopup({ entity, row, teamSlug, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!row) return null;

  const known = new Map((RECORD_COLUMNS[entity] || []).map((c) => [c.key, c]));
  // The page's columns first, in the page's order; then everything else the
  // record carries, so nothing the query returned is hidden from the reader.
  const keys = [...known.keys(), ...Object.keys(row).filter((k) => !known.has(k) && !SKIP.has(k))];
  const rel = ROW_LINKS[entity] ? ROW_LINKS[entity](row) : null;
  const href = rel && teamSlug ? `/${encodeURIComponent(teamSlug)}${rel}` : null;

  return (
    <div role="dialog" aria-label={`${entity} record`} onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className="trodo-record-popup" style={{ width: 'min(520px, 92vw)', height: '100%', overflowY: 'auto', background: 'var(--surface-base, #111)', borderLeft: `1px solid ${theme.border}`, padding: 18, display: 'grid', alignContent: 'start', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{entity.replace(/s$/, '')}</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.textPrimary, marginTop: 2, wordBreak: 'break-word' }}>{String(titleOf(entity, row))}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', padding: 4 }}><X size={16} /></button>
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {keys.map((k) => {
            const v = row[k];
            if (v == null || v === '') return null;
            const col = known.get(k);
            const shown = col ? formatCell(v, col.format) : (typeof v === 'object' ? JSON.stringify(v, null, 1) : String(v));
            const long = String(shown).length > 120 || /\n/.test(String(shown));
            return (
              <div key={k} style={{ display: 'grid', gridTemplateColumns: long ? '1fr' : '140px 1fr', gap: 8, fontSize: 13, alignItems: 'baseline' }}>
                <span style={{ color: theme.textSecondary }}>{col ? col.label : k.replace(/_/g, ' ')}</span>
                <span style={{ color: theme.textPrimary, whiteSpace: long ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: long ? 'break-word' : 'normal' }} title={typeof v === 'string' && !long ? v : undefined}>{shown}</span>
              </div>
            );
          })}
        </div>
        {href && (
          <Link to={href} onClick={besideClick(href)} style={{ fontSize: 12, color: theme.primary, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
            Open full page <ExternalLink size={12} />
          </Link>
        )}
      </div>
    </div>
  );
}
